'use client';

import { useState, useEffect } from 'react';
import {
	XMarkIcon,
	BellIcon,
	CheckIcon,
	ClockIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';
import { format } from 'date-fns';

const NotificationsModal = ({ closeModal }) => {
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchNotifications();
	}, []);

	const fetchNotifications = async () => {
		try {
			setLoading(true);
			// Mock notifications for now - replace with actual API call
			const mockNotifications = [
				{
					id: 1,
					type: 'DEBT_REMINDER',
					title: 'Payment Due Soon',
					message: 'Debt payment of ₦50,000 is due in 3 days',
					read: false,
					createdAt: new Date().toISOString(),
				},
				{
					id: 2,
					type: 'PAYMENT_RECEIVED',
					title: 'Payment Received',
					message: 'Payment of ₦25,000 has been received',
					read: true,
					createdAt: new Date(Date.now() - 86400000).toISOString(),
				},
				{
					id: 3,
					type: 'SYSTEM_ALERT',
					title: 'System Maintenance',
					message: 'Scheduled maintenance on Sunday 2AM - 4AM',
					read: false,
					createdAt: new Date(Date.now() - 172800000).toISOString(),
				},
			];
			setNotifications(mockNotifications);
		} catch (error) {
			console.error('Error fetching notifications:', error);
		} finally {
			setLoading(false);
		}
	};

	const markAsRead = async (notificationId) => {
		try {
			// API call to mark as read
			setNotifications((prev) =>
				prev.map((notif) =>
					notif.id === notificationId
						? { ...notif, read: true }
						: notif,
				),
			);
		} catch (error) {
			console.error('Error marking notification as read:', error);
		}
	};

	const markAllAsRead = async () => {
		try {
			// API call to mark all as read
			setNotifications((prev) =>
				prev.map((notif) => ({ ...notif, read: true })),
			);
		} catch (error) {
			console.error('Error marking all notifications as read:', error);
		}
	};

	const getNotificationIcon = (type) => {
		switch (type) {
			case 'DEBT_REMINDER':
				return <ClockIcon className='h-5 w-5 text-yellow-500' />;
			case 'PAYMENT_RECEIVED':
				return <CheckIcon className='h-5 w-5 text-green-500' />;
			case 'SYSTEM_ALERT':
				return <BellIcon className='h-5 w-5 text-blue-500' />;
			default:
				return <BellIcon className='h-5 w-5 text-gray-500' />;
		}
	};

	return (
		<div className='fixed inset-0 z-50 overflow-y-auto'>
			<div className='flex min-h-screen items-center justify-center p-4'>
				<div
					className='fixed inset-0 modal-backdrop'
					onClick={closeModal}></div>

				<div className='relative bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden'>
					{/* Header */}
					<div className='flex items-center justify-between p-6 border-b border-gray-200'>
						<h2 className='text-lg font-semibold text-gray-900'>
							Notifications
						</h2>
						<div className='flex items-center space-x-2'>
							{notifications.some((n) => !n.read) && (
								<button
									onClick={markAllAsRead}
									className='text-sm text-blue-600 hover:text-blue-700 font-medium'>
									Mark all read
								</button>
							)}
							<button
								onClick={closeModal}
								className='p-1 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors'>
								<XMarkIcon className='h-5 w-5' />
							</button>
						</div>
					</div>

					{/* Content */}
					<div className='max-h-96 overflow-y-auto'>
						{loading ? (
							<div className='flex items-center justify-center py-8'>
								<LoadingSpinner size='lg' />
							</div>
						) : notifications.length === 0 ? (
							<div className='text-center py-8'>
								<BellIcon className='h-12 w-12 text-gray-300 mx-auto mb-4' />
								<p className='text-gray-500'>
									No notifications
								</p>
							</div>
						) : (
							<div className='divide-y divide-gray-200'>
								{notifications.map((notification) => (
									<div
										key={notification.id}
										className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
											!notification.read
												? 'bg-blue-50'
												: ''
										}`}
										onClick={() =>
											!notification.read &&
											markAsRead(notification.id)
										}>
										<div className='flex items-start space-x-3'>
											<div className='flex-shrink-0 mt-1'>
												{getNotificationIcon(
													notification.type,
												)}
											</div>
											<div className='flex-1 min-w-0'>
												<div className='flex items-center justify-between'>
													<p
														className={`text-sm font-medium ${
															!notification.read
																? 'text-gray-900'
																: 'text-gray-700'
														}`}>
														{notification.title}
													</p>
													{!notification.read && (
														<div className='w-2 h-2 bg-blue-500 rounded-full flex-shrink-0'></div>
													)}
												</div>
												<p className='text-sm text-gray-600 mt-1'>
													{notification.message}
												</p>
												<p className='text-xs text-gray-400 mt-2'>
													{format(
														new Date(
															notification.createdAt,
														),
														"MMM dd, yyyy 'at' h:mm a",
													)}
												</p>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Footer */}
					{notifications.length > 0 && (
						<div className='p-4 border-t border-gray-200 bg-gray-50'>
							<button
								onClick={closeModal}
								className='w-full text-center text-sm text-gray-600 hover:text-gray-700 font-medium'>
								View all notifications
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default NotificationsModal;
